var ACCESS_TOKEN_KEY = 'alleflow_access_token';
var SAVED_CREDENTIALS_KEY = 'alleflow_saved_credentials';
var _embedWorkflowId = '';

function getResolvedApiOrigin() {
  var configured = String(process.env.REACT_APP_API_URL || '').trim();
  if (configured) {
    return configured.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location) {
    var protocol = window.location.protocol || 'http:';
    var hostname = window.location.hostname || 'localhost';
    var port = window.location.port || '';
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000') {
      return protocol + '//' + hostname + ':4000';
    }
  }

  return '';
}

function getApiBasePath() {
  return getResolvedApiOrigin() + '/api';
}

function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY) || '';
  } catch (error) {
    return '';
  }
}

function setAccessToken(token) {
  try {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  } catch (error) {
    // Ignore localStorage errors in non-browser environments.
  }
}

function createRequestError(message, status) {
  var error = new Error(message || ('Request failed with status ' + status));
  error.status = status;
  error.statusCode = status;
  return error;
}

function setEmbedWorkflowId(workflowId) {
  _embedWorkflowId = String(workflowId || '');
}

function getEmbedWorkflowId() {
  return _embedWorkflowId;
}

function request(path, options) {
  var token = getAccessToken();
  var headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = 'Bearer ' + token;
  }

  if (_embedWorkflowId) {
    headers['x-embed-workflow-id'] = _embedWorkflowId;
  }

  if (options && options.headers) {
    Object.keys(options.headers).forEach(function(name) {
      headers[name] = options.headers[name];
    });
  }

  return fetch(getApiBasePath() + path, {
    method: (options && options.method) || 'GET',
    headers: headers,
    body: options && options.body ? JSON.stringify(options.body) : undefined
  }).then(function(response) {
    if (!response.ok) {
      return response.text().then(function(text) {
        var message = text;
        try {
          var parsed = JSON.parse(text || '{}');
          if (parsed && parsed.error) message = parsed.error;
        } catch (error) {
          // Keep original text as message.
        }

        throw createRequestError(message, response.status);
      });
    }

    if (response.status === 204) return null;
    return response.json();
  });
}

function saveCredentials(username, password) {
  try {
    localStorage.setItem(SAVED_CREDENTIALS_KEY, JSON.stringify({ username: username, password: password }));
  } catch (error) {
    // Ignore localStorage errors.
  }
}

function getSavedCredentials() {
  try {
    var stored = localStorage.getItem(SAVED_CREDENTIALS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // Ignore errors.
  }
  return null;
}

function clearSavedCredentials() {
  try {
    localStorage.removeItem(SAVED_CREDENTIALS_KEY);
  } catch (error) {
    // Ignore errors.
  }
}

var PURE_BASE64_RE = /^[A-Za-z0-9+/\r\n\s]{100,}={0,2}\s*$/;
var DATA_URI_RE = /^data:[^;]{1,60};base64,/;
var BASE64_PREVIEW_LEN = 80;
function minimizeStringBase64(val) {
  if (typeof val !== 'string') return val;
  var dataMatch = val.match(DATA_URI_RE);
  if (dataMatch) {
    var prefix = dataMatch[0];
    var payload = val.slice(prefix.length);
    if (payload.length > BASE64_PREVIEW_LEN) {
      return prefix + payload.slice(0, BASE64_PREVIEW_LEN) + '.....<more>';
    }
    return val;
  }
  if (val.length > BASE64_PREVIEW_LEN && PURE_BASE64_RE.test(val)) {
    var clean = val.replace(/[\r\n\s]/g, '');
    return clean.slice(0, BASE64_PREVIEW_LEN) + '.....<more>';
  }
  return val;
}

var MINIMIZE_MAX_TOTAL_CHARS = 20000;
var MINIMIZE_MAX_DEPTH = 10;
// A truncated array always keeps at least this many sample items so the AI can
// see the element's shape (which fields exist, what they look like) — a single
// collapsed item isn't enough to understand the structure. Applied only to
// top-level arrays (the data the model reads as inputs[0]); deeper arrays stay
// strict (1 sample) so nested data can't re-bloat the payload past the cap.
var MINIMIZE_MIN_SAMPLE_ITEMS = 3;
var MINIMIZE_TOP_LEVEL_DEPTH = 1;

function minimizeMeasureChars(value) {
  try {
    return JSON.stringify(value).length;
  } catch (e) {
    return String(value).length;
  }
}

function minimizeValue(value, depth, budget) {
  if (depth > MINIMIZE_MAX_DEPTH) return value;
  if (typeof value === 'string') return minimizeStringBase64(value);
  if (Array.isArray(value)) return minimizeArrayWithBudget(value, depth, budget);
  if (value !== null && typeof value === 'object') {
    var result = {};
    var keys = Object.keys(value);
    for (var i = 0; i < keys.length; i++) {
      result[keys[i]] = minimizeValue(value[keys[i]], depth + 1, budget);
    }
    return result;
  }
  return value;
}

function minimizeArrayWithBudget(arr, depth, budget) {
  if (arr.length === 0) return arr;
  // Keep more samples for top-level arrays so their shape survives; deeper
  // arrays keep the bare minimum of 1 to stay within the budget.
  var minSamples = depth <= MINIMIZE_TOP_LEVEL_DEPTH ? MINIMIZE_MIN_SAMPLE_ITEMS : 1;
  var kept = [];
  for (var i = 0; i < arr.length; i++) {
    var before = budget.remaining;
    var minimizedItem = minimizeValue(arr[i], depth + 1, budget);
    // Nested arrays inside this item already charged the budget; only bill the
    // part not yet counted so nested content isn't double-charged (which made
    // the budget drain ~2x fast and dropped items the cap should have allowed).
    var chargedByNested = before - budget.remaining;
    var residualCost = (minimizeMeasureChars(minimizedItem) + 1) - chargedByNested;
    if (residualCost < 0) residualCost = 0;
    if (residualCost > budget.remaining && kept.length >= minSamples) {
      // String marker (never an object field) so no AI mistakes it for real data.
      kept.push('… ' + (arr.length - i) + ' more items (truncated for display — not real data)');
      return kept;
    }
    budget.remaining -= residualCost;
    kept.push(minimizedItem);
  }
  return kept;
}

function minimizeArrays(value) {
  var budget = { remaining: MINIMIZE_MAX_TOTAL_CHARS };
  return minimizeValue(value, 0, budget);
}

function requestStream(path, options, handlers) {
  var h = handlers || {};
  var token = getAccessToken();
  var headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers.Authorization = 'Bearer ' + token;
  }
  if (_embedWorkflowId) {
    headers['x-embed-workflow-id'] = _embedWorkflowId;
  }
  var abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

  // Stall watchdog. The SSE reader below blocks on read() with no built-in
  // timeout, so a hung upstream (a slow/unreachable model API, a dropped
  // socket, or the dev API restarting mid-stream) would otherwise spin the UI
  // forever with zero bytes and zero error. We arm a timer that is reset on
  // every received chunk; if nothing arrives for stallMs we abort the fetch and
  // surface a real error so the caller can stop its spinner and let the user
  // retry. Override per-call with options.stallTimeoutMs.
  var stallMs = (options && options.stallTimeoutMs) || 60000;
  var stallTimer = null;
  var stalled = false;
  function clearStall() {
    if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
  }
  function armStall() {
    clearStall();
    stallTimer = setTimeout(function() {
      stalled = true;
      stallTimer = null;
      if (h.onError) {
        h.onError(createRequestError('No response from the server for ' + Math.round(stallMs / 1000) + 's — the request stalled. Please try again.', 0));
      }
      if (abortController) abortController.abort();
    }, stallMs);
  }

  fetch(getApiBasePath() + path, {
    method: (options && options.method) || 'POST',
    headers: headers,
    body: options && options.body ? JSON.stringify(options.body) : undefined,
    signal: abortController ? abortController.signal : undefined
  }).then(function(response) {
    if (!response.ok) {
      throw createRequestError('Stream request failed', response.status);
    }
    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';
    armStall();

    function processChunks() {
      return reader.read().then(function(result) {
        if (result.done) {
          clearStall();
          if (h.onClose) h.onClose();
          return;
        }
        armStall(); // bytes are flowing — reset the watchdog
        buffer += decoder.decode(result.value, { stream: true });
        var lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (var i = 0; i < lines.length; i++) {
          var line = lines[i].trim();
          if (!line || line.startsWith(':')) continue;
          if (line.startsWith('data: ')) {
            try {
              var payload = JSON.parse(line.slice(6));
              if (h.onEvent) h.onEvent(payload);
            } catch (e) {
              // ignore malformed
            }
          }
        }
        return processChunks();
      });
    }

    return processChunks();
  }).catch(function(err) {
    clearStall();
    if (stalled) return; // already surfaced by the watchdog
    if (err && err.name === 'AbortError') return;
    if (h.onError) h.onError(err);
  });

  return function abort() {
    clearStall();
    if (abortController) abortController.abort();
  };
}

module.exports = {
  request: request,
  requestStream: requestStream,
  getApiBasePath: getApiBasePath,
  getApiOrigin: getResolvedApiOrigin,
  getAccessToken: getAccessToken,
  setAccessToken: setAccessToken,
  setEmbedWorkflowId: setEmbedWorkflowId,
  getEmbedWorkflowId: getEmbedWorkflowId,
  saveCredentials: saveCredentials,
  getSavedCredentials: getSavedCredentials,
  clearSavedCredentials: clearSavedCredentials,
  minimizeArrays: minimizeArrays,
  minimizeStringBase64: minimizeStringBase64
};
